import React                   from 'react';
import { Observe }             from 'rewire-core';
import {
  WithStyle,
  MixedMenu,
  IMenuBaseItem,
  IActionMenuItem,
  IToggleMenuItem,
}                              from 'rewire-ui';
import Avatar                  from '@material-ui/core/Avatar';
import { PopoverOrigin }       from '@material-ui/core/Popover';
import { Theme }               from '@material-ui/core/styles';
import AccountCircleIcon       from '@material-ui/icons/AccountCircleOutlined';
import ExitToAppIcon           from '@material-ui/icons/ExitToApp';
import QuickStartIcon          from '@material-ui/icons/MenuBookOutlined';
import HelpIcon                from '@material-ui/icons/HelpOutline';
import InfoIcon                from '@material-ui/icons/InfoOutlined';
import KeyboardIcon            from '@material-ui/icons/KeyboardOutlined';
import MessageIcon             from '@material-ui/icons/MessageOutlined';
import SwitchIcon              from '@material-ui/icons/SwapHorizontalCircleOutlined';
import SettingsIcon            from '@material-ui/icons/SettingsOutlined';
import TestModeToggleIcon      from '@material-ui/icons/SwapHorizontalCircleOutlined';
import { history }             from '../RouterWithHistory';
import Routes                  from '../Routes';
import AboutDialog             from './AboutDialog';
import SwitchLocations         from '../accounts/SwitchLocations';
import userOptionsMenuModel    from './UserOptionsMenuModel';
import UserProfileDialog       from './UserProfileDialog';
import LogIcon                 from '@material-ui/icons/AssignmentOutlined';
import { withStyles }          from '../utility/styles';
import auditLogDialogModel     from '../audit/AuditLogDialogModel';
import AuditLogDialog          from '../audit/AuditLogDialog';

const styles = (theme: Theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
  },
  userOptionsButton: {
    marginLeft: '10px',
    padding: '0px',
    minHeight: '0px',
    '&:hover, &:active': {
      background: 'transparent',
    },
  },
  userOptionsAvatar: {
    width: '36px',
    height: '36px',
  },
  userOptionsAvatarColorDefault: {
    backgroundColor: theme.palette.dialogPrimary.light,
  },
  listItemText: {
    fontSize: 'inherit',
  },
});

interface IUserOptionsMenuProps {
}

type UserOptionsMenuProps = WithStyle<ReturnType<typeof styles>, IUserOptionsMenuProps>;

class UserOptionsMenu extends React.Component<UserOptionsMenuProps> {
  constructor(props: UserOptionsMenuProps) {
    super(props);
  }

  shouldComponentUpdate(_nextProps: UserOptionsMenuProps) {
    return false;
  }

  handleProfileClick = () => {
    userOptionsMenuModel.showUserProfileDialog();
  };

  handleSettingsClick = () => {
    history.push(Routes.Settings);
  };

  handleShortcutsClick = () => {
  };

  handleGiveFeedbackClick = () => {
  };

  handleQuickStartGuideClick = () => {
  };

  handleGetHelpClick = () => {
  };

  toggleTheme = () => {
    if (document.documentElement.style.filter) document.documentElement.style.filter = '';
    else document.documentElement.style.filter = 'invert(1) hue-rotate(180deg)';
  };

  handleToggleTestMode = () => {
    userOptionsMenuModel.toggleTestMode();
  };

  handleAboutClick = () => {
    userOptionsMenuModel.showAboutDialog();
  };

  handleSwitchLocation = () => {
    userOptionsMenuModel.switchLocationDialog();
  };

  handleAuditLogsClick = () => {
    auditLogDialogModel.load();
  };

  handleLogoutClick = async () => {
    await userOptionsMenuModel.logout();
  };

  renderButtonContent = (): JSX.Element => {
    const {classes} = this.props;
    return <Observe render={() => (
      <Avatar className={classes.userOptionsAvatar} classes={{colorDefault: classes.userOptionsAvatarColorDefault}} src={userOptionsMenuModel.avatar}>{userOptionsMenuModel.userInitials}</Avatar>
    )} />;
  };

  render() {
    const {classes}                        = this.props;
    const anchorOrigin:    PopoverOrigin   = {vertical: 'bottom', horizontal: 'right'};
    const transformOrigin: PopoverOrigin   = {vertical: 'top',    horizontal: 'right'};
    const items:           IMenuBaseItem[] = [
      {name: 'viewProfile',       title: 'View Profile',            icon: AccountCircleIcon,  divider: false, onClick: this.handleProfileClick} as IActionMenuItem,
      {name: 'settings',          title: 'Settings',                icon: SettingsIcon,       divider: false, onClick: this.handleSettingsClick,  closeOnClick: true} as IActionMenuItem,
      {name: 'switchLocations',   title: 'Switch Locations',        icon: SwitchIcon,         divider: true,  onClick: this.handleSwitchLocation, closeOnClick: true, disabled: (() => !userOptionsMenuModel.canSwitchLocation())} as IActionMenuItem,
      {name: 'quickStartGuide',   title: 'Quick Start Guide',       icon: QuickStartIcon,     divider: false, onClick: this.handleQuickStartGuideClick, isExternalLink: true, href: 'https://zendesk.worksight.net/hc/en-us/articles/13960047884820-Quick-Start-Guide'} as IActionMenuItem,
      {name: 'getHelp',           title: 'Get Help',                icon: HelpIcon,           divider: false, onClick: this.handleGetHelpClick,         isExternalLink: true, href: 'https://zendesk.worksight.net/hc/en-us/categories/360006550851-WorkSight-FLOW'} as IActionMenuItem,
      {name: 'keyboardShortcuts', title: 'Keyboard Shortcuts',      icon: KeyboardIcon,       divider: false, onClick: this.handleShortcutsClick,       isExternalLink: true, href: 'https://zendesk.worksight.net/hc/en-us/articles/360054496591-Keyboard-Shortcuts-in-WorkSight-Flow'} as IActionMenuItem,
      {name: 'giveFeedback',      title: 'Give Feedback',           icon: MessageIcon,        divider: true,  onClick: this.handleGiveFeedbackClick,    isExternalLink: true, href: 'https://zendesk.worksight.net/hc/en-us/community/topics/360000756211-WorkSight-5-Feedback'} as IActionMenuItem,
      {name: 'toggleTestMode',    title: 'Toggle Test Mode',        icon: TestModeToggleIcon, divider: true,  onClick: this.handleToggleTestMode, disabled:() => !userOptionsMenuModel.canToggleTestMode(), active: () => userOptionsMenuModel.testModeEnabled} as IToggleMenuItem,
      {name: 'about',             title: 'About',                   icon: InfoIcon,           divider: false, onClick: this.handleAboutClick} as IActionMenuItem,
      {name: 'auditLogs',         title: 'View Audit Logs',         icon: LogIcon,            divider: false, onClick: this.handleAuditLogsClick, closeOnClick: true} as IActionMenuItem,
      {name: 'logout',            title: 'Logout',                  icon: ExitToAppIcon,      divider: false, onClick: this.handleLogoutClick,    closeOnClick: true} as IActionMenuItem,
    ];

    if ((window as any).chrome) {
      items.splice(7, 0, {name: 'darkMode', title: 'Toggle Light/Dark Theme', icon: SwitchIcon, divider: false, onClick: this.toggleTheme, closeOnClick: true});
    }

    return <Observe render={() => (
      <div className={classes.root}>
        {/* <Typography variant='body2'>{userOptionsAvatar.locationName} Workspace</Typography> */}
        <MixedMenu
          menuId='user-menu'
          items={items}
          buttonContent={<this.renderButtonContent />}
          buttonProps={{disableRipple: true}}
          classes={{menuButton: classes.userOptionsButton, listItemText: classes.listItemText}}
          getContentAnchorEl={null}
          anchorOrigin={anchorOrigin}
          transformOrigin={transformOrigin}
          // TransitionComponent={Slide}
          // TransitionProps={{direction: 'down'}}
        />
        <UserProfileDialog />
        <AboutDialog />
        <SwitchLocations />
        <AuditLogDialog />
      </div>
    )} />;
  }
}

export default withStyles<IUserOptionsMenuProps>(styles, UserOptionsMenu);
